import { z } from "zod";

export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
}

export interface SubscriberFormData {
  email: string;
}

const contactFormSchema = z.object({
  name: z.string()
    .min(2, "Имя должно содержать минимум 2 символа")
    .max(100, "Имя не должно превышать 100 символов")
    .regex(/^[a-zA-Zа-яА-Я\s-]+$/, "Имя может содержать только буквы, пробелы и дефисы"),
  
  email: z.string()
    .email("Введите корректный email адрес")
    .max(100, "Email не должен превышать 100 символов"),
  
  phone: z.string()
    .regex(/^\+?[0-9\s-()]+$/, "Введите корректный номер телефона")
    .max(20, "Номер телефона не должен превышать 20 символов")
    .optional()
    .or(z.literal("")),
  
  company: z.string()
    .max(100, "Название компании не должно превышать 100 символов")
    .optional()
    .or(z.literal("")),
  
  message: z.string()
    .min(10, "Сообщение должно содержать минимум 10 символов")
    .max(1000, "Сообщение не должно превышать 1000 символов")
    .regex(/^[^<>]*$/, "Сообщение не должно содержать HTML-теги"),
}).refine((data): data is ContactFormData => {
  return typeof data.name === 'string' && 
         typeof data.email === 'string' && 
         typeof data.message === 'string';
});

const subscriberFormSchema = z.object({
  email: z.string()
    .email("Введите корректный email адрес")
    .max(100, "Email не должен превышать 100 символов"),
}).refine((data): data is SubscriberFormData => {
  return typeof data.email === 'string';
});

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationSuccess {
  success: true;
  data: ContactFormData | SubscriberFormData;
}

export interface ValidationFailure {
  success: false;
  errors: ValidationError[];
}

export type ValidationResult = ValidationSuccess | ValidationFailure;

export const validateContactForm = (data: unknown): ValidationResult => {
  try {
    const validatedData = contactFormSchema.parse(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        errors: error.errors.map(err => ({
          field: err.path.join("."),
          message: err.message
        }))
      };
    }
    return {
      success: false,
      errors: [{ field: "unknown", message: "Произошла ошибка при валидации формы" }]
    };
  }
};

export const validateSubscriberForm = (data: unknown): ValidationResult => {
  try {
    const validatedData = subscriberFormSchema.parse(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        errors: error.errors.map(err => ({
          field: err.path.join("."),
          message: err.message
        }))
      };
    }
    return {
      success: false,
      errors: [{ field: "unknown", message: "Произошла ошибка при валидации формы" }]
    };
  }
};